<template>
  <div class="expiration-icon-wrapper">
    <div ref="iconActivator" @click="toggleEditor" :class="{ 'read-only-icon': props.icon === 'read_only' }">
      <EXTooltip
        v-if="showTooltip"
        :title="tooltipTitle"
        :description="tooltipDescription"
        placement="bottom"
        :children="true"
        :delay="300"
      >
        <div 
          class="expiration-icon-container"
          :data-icon="props.icon"
          :data-badge-level="props?.badgeLevel?.toLowerCase() || ''"
          slot="icon"
        >
          <Icon :name="iconName" class="default-icon" :size="props.size" />
          <Icon :name="hoverIconName" class="hover-icon" :size="props.size" />
        </div>
      </EXTooltip>
      <div 
        v-else
        class="expiration-icon-container"
        :data-icon="props.icon"
        :data-badge-level="props?.badgeLevel?.toLowerCase() || ''"
      >
        <Icon :name="iconName" class="default-icon" :size="props.size" />
        <Icon :name="hoverIconName" class="hover-icon" :size="props.size" />
      </div>
    </div>
    
    <v-menu
      v-model="showEditor"
      :close-on-content-click="false"
      :position-x="xPosition"
      :position-y="yPosition"
      :top="displayTop"
      absolute
      :nudge-top="-4"
      attach="body"
      :content-class="`expiration-editor-menu expiration-editor-menu-${_uid}`"
    >
      <ExpirationDateEditor
        :days="props.days"
        :badge-status="props.badgeStatus"
        :icon="props.icon"
        :action="editorAction"
        :policy="props.policy"
        :date="props.expirationDate"
        :triangle-position="trianglePosition"
        :triangle-align="triangleAlign"
        :triangle-offset="triangleOffset"
        @close="closeEditor"
        @save="handleSave"
        @remove="handleRemove"
        @setToGoodStanding="handleSetToGoodStanding"
        @makeObsolete="handleMakeObsolete"
      />
    </v-menu>
  </div>
</template>
<script setup>
import { computed, ref, nextTick, getCurrentInstance, watch, onBeforeUnmount } from "vue";
import Icon from "@components/icons/Icon.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import ExpirationDateEditor from "@components/ExpirationDateEditor/ExpirationDateEditor.vue";

const props = defineProps({
  icon: {
    type: String,
    required: true,
    default: "add",
    validator: (value) => {
      return ["add", "edit", "if_obsolete", "soon", "tonight", "read_only"].includes(value);
    },
  },
  badgeLevel: {
    type: String,
    required: true,
    validator: (value) => {
      return ["apprentice", "professional", "coach", "master"].includes(value.toLowerCase());
    },
  },
  size: {
    type: String,
    default: "normal",
    validator: (value) => {
      return ["normal", "small"].includes(value);
    },
  },
  expirationDate: {
    type: [String, Date],
    default: null,
  },
  days: {
    type: Number,
    default: 0,
  },
  badgeStatus: {
    type: String,
    default: "Given",
    validator: (value) => {
      return ["Given", "Good Standing", "Obsolete"].includes(value);
    },
  },
  policy: {
    type: String,
    default: "Date",
    validator: (value) => {
      return ["Date", "Days"].includes(value);
    },
  },
  readOnly: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["save", "remove", "setToGoodStanding", "makeObsolete"]);

const instance = getCurrentInstance();
const _uid = instance?.uid || 0;

const showEditor = ref(false);
const iconActivator = ref(null);
const xPosition = ref(0);
const yPosition = ref(0);
const displayTop = ref(false);
const trianglePosition = ref("top");
const triangleAlign = ref("right");
const triangleOffset = ref("24px");
const initialScrollPositions = ref({ x: 0, y: 0 });

const iconName = computed(() => {
  return `expiration_icon_${props.icon}`;
});

const hoverIconName = computed(() => {
  if (!props?.badgeLevel || props?.badgeLevel?.toLowerCase() === "default") {
    return `expiration_icon_${props.icon}`;
  }
  return `expiration_icon_${props.icon}_${props.badgeLevel.toLowerCase()}`;
});

const showTooltip = computed(() => {
  if (props.readOnly && props.icon === "add") return false;
  if (props.readOnly && props.icon === "edit") return true;
  if (props.readOnly && props.icon === "if_obsolete") return true;
  if (props.readOnly && props.icon === "soon") return true;
  if (props.readOnly && props.icon === "tonight") return true;
  if (props.readOnly && props.icon === "read_only") return true;
  if (!props.readOnly && props.icon === "add") return true;
  if (!props.readOnly && props.icon === "edit") return true;
  if (!props.readOnly && props.icon === "if_obsolete") return true;
  if (!props.readOnly && props.icon === "soon") return true;
  if (!props.readOnly && props.icon === "tonight") return true;
  if (!props.readOnly && props.icon === "read_only") return true;
  return ["add", "edit", "if_obsolete", "soon", "tonight", "read_only"].includes(props.icon);
});

const formattedExpirationDate = computed(() => {
  if (!props.expirationDate) return '';
  const date = new Date(props.expirationDate);
  if (isNaN(date)) return '';
  return `${date.getDate()}-${date.toLocaleString('default', { month: 'short' })}-${date.getFullYear()}`;
});

const daysUntilExpiration = computed(() => {
  if (!props.expirationDate) return null;
  const now = new Date();
  const exp = new Date(props.expirationDate);
  now.setHours(0,0,0,0);
  exp.setHours(0,0,0,0);
  const diff = Math.round((exp - now) / (1000 * 60 * 60 * 24));
  return diff >= 0 ? diff : null;
});

const tooltipTitle = computed(() => {
  if (props.readOnly && props.icon === "add") {
    return "";
  } else if (props.readOnly && props.icon === "edit") {
    return props.expirationDate ? `Expires in ${daysUntilExpiration.value} days` : "Expires soon";
  } else if (props.readOnly && props.icon === "if_obsolete") {
    return "Expires after becoming Obsolete";
  } else if (props.readOnly && props.icon === "soon") {
    return "Expires Soon";
  } else if (props.readOnly && props.icon === "tonight") {
    return "Expires Today";
  } else if (props.readOnly && props.icon === "read_only") {
    return "Does not Expire";
  } else if (!props.readOnly && props.icon === "add") {
    return "Add Expiration Date";
  } else if (!props.readOnly && props.icon === "edit") {
    return props.expirationDate ? `Expires in ${daysUntilExpiration.value} days` : "Expires soon";
  } else if (!props.readOnly && props.icon === "if_obsolete") {
    return "Expires after becoming Obsolete";
  } else if (!props.readOnly && props.icon === "soon") {
    return "Expires Soon";
  } else if (!props.readOnly && props.icon === "tonight") {
    return "Expires Today";
  } else if (!props.readOnly && props.icon === "read_only") {
    return "Does not Expire";
  } else {
    return "";
  }
});

const tooltipDescription = computed(() => {
  if (props.readOnly && props.icon === "add") {
    return "";
  } else if (props.readOnly && props.icon === "edit") {
    return props.expirationDate ? `Set to expire on ${formattedExpirationDate.value}` : "Set to expire soon";
  } else if (props.readOnly && props.icon === "if_obsolete") {
    return `Set to expire ${daysUntilExpiration.value} days after`;
  } else if (props.readOnly && props.icon === "soon") {
    return `Set to expire in ${daysUntilExpiration.value} days`;
  } else if (props.readOnly && props.icon === "tonight") {
    return "";
  } else if (props.readOnly && props.icon === "read_only") {
    return "";
  } else if (!props.readOnly && props.icon === "add") {
    return "";
  } else if (!props.readOnly && props.icon === "edit") {
    return props.expirationDate ? `Set to expire on ${formattedExpirationDate.value}` : "Set to expire soon";
  } else if (!props.readOnly && props.icon === "if_obsolete") {
    return `Set to expire ${daysUntilExpiration.value} days after`;
  } else if (!props.readOnly && props.icon === "soon") {
    return `Set to expire in ${daysUntilExpiration.value} days`;
  } else if (!props.readOnly && props.icon === "tonight") {
    return "";
  } else {
    return "";
  }
});

const editorAction = computed(() => {
  if (props.icon === "add") return "Add";
  if (props.icon === "edit") return "Save";
  if (props.icon === "read_only") return "View";
  return "View";
});

const calculatePosition = () => {
  if (!iconActivator.value) return;

  const rect = iconActivator.value.getBoundingClientRect();
  const iconLeft = rect.left;
  const activatorWidth = rect.width;
  
  // Popover width is 300px (from ExpirationDateEditor.vue)
  const popoverWidth = 300;
  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;
  const padding = 16;
  
  // Estimate popover height (approximate based on content)
  // This prevents page scroll by calculating position before opening
  const estimatedPopoverHeight = 400; // Approximate height of editor
  
  const spaceBelow = viewportHeight - rect.bottom;
  const spaceAbove = rect.top;
  
  // Decide if we should display on top or bottom BEFORE positioning
  if (spaceBelow < estimatedPopoverHeight && spaceAbove > spaceBelow) {
    // Not enough space below and more space above - display on top
    displayTop.value = true;
    trianglePosition.value = 'bottom';
    yPosition.value = rect.top - 4; // Position above with small gap
  } else {
    // Default: display below
    displayTop.value = false;
    trianglePosition.value = 'top';
    yPosition.value = rect.bottom + 4; // Position below with small gap
  }
  
  // Default to right alignment like DropDownMenu
  // Position popover so the triangle at 24px from right edge aligns with icon center
  triangleAlign.value = 'right';
  triangleOffset.value = '24px';
  xPosition.value = iconLeft - popoverWidth + (activatorWidth / 2) + 24;
  
  // Check if popover goes off the left edge
  if (xPosition.value < padding) {
    // Switch to left alignment
    triangleAlign.value = 'left';
    triangleOffset.value = '24px';
    xPosition.value = iconLeft + (activatorWidth / 2) - 24;
    
    // If still doesn't fit, adjust
    if (xPosition.value + popoverWidth > viewportWidth - padding) {
      // Calculate dynamic triangle offset
      const iconCenterX = iconLeft + (activatorWidth / 2);
      xPosition.value = padding;
      const triangleOffsetPx = iconCenterX - xPosition.value;
      triangleOffset.value = `${Math.max(20, Math.min(triangleOffsetPx, popoverWidth - 20))}px`;
    }
  }
};

const handleScroll = (event) => {
  if (!showEditor.value) return;
  
  const scrollThreshold = 50; // pixels - allow 50px of scroll before closing
  const target = event.target;
  
  let currentScrollX = 0;
  let currentScrollY = 0;
  
  if (target === window || target === document) {
    currentScrollX = window.scrollX || window.pageXOffset;
    currentScrollY = window.scrollY || window.pageYOffset;
  } else {
    currentScrollX = target.scrollLeft;
    currentScrollY = target.scrollTop;
  }
  
  const scrollDeltaX = Math.abs(currentScrollX - initialScrollPositions.value.x);
  const scrollDeltaY = Math.abs(currentScrollY - initialScrollPositions.value.y);
  
  // If scrolled more than threshold, close
  if (scrollDeltaX > scrollThreshold || scrollDeltaY > scrollThreshold) {
    closeEditor();
  } else {
    // Within threshold - update position to follow icon
    calculatePosition();
  }
};

const toggleEditor = () => {
  if (props.icon === "read_only") return;
  if (!showEditor.value) {
    calculatePosition();
    
    // Capture initial scroll positions when opening
    const mainContainer = document.querySelector('.ex-main-container');
    const tableWrapper = document.querySelector('.v-data-table__wrapper');
    
    if (mainContainer) {
      initialScrollPositions.value.x = mainContainer.scrollLeft;
      initialScrollPositions.value.y = mainContainer.scrollTop;
    } else if (tableWrapper) {
      initialScrollPositions.value.x = tableWrapper.scrollLeft;
      initialScrollPositions.value.y = tableWrapper.scrollTop;
    } else {
      initialScrollPositions.value.x = window.scrollX || window.pageXOffset;
      initialScrollPositions.value.y = window.scrollY || window.pageYOffset;
    }
  }
  showEditor.value = !showEditor.value;
};

const closeEditor = () => {
  showEditor.value = false;
};

// Watch for showEditor changes to add/remove scroll listeners
watch(showEditor, (newVal) => {
  const scrollContainers = [
    document.querySelector('.ex-main-container'),
    document.querySelector('.v-data-table__wrapper'),
    window
  ];
  
  scrollContainers.forEach(container => {
    if (container) {
      if (newVal) {
        container.addEventListener('scroll', handleScroll, true);
      } else {
        container.removeEventListener('scroll', handleScroll, true);
      }
    }
  });
});

// Cleanup on component unmount
onBeforeUnmount(() => {
  const scrollContainers = [
    document.querySelector('.ex-main-container'),
    document.querySelector('.v-data-table__wrapper'),
    window
  ];
  
  scrollContainers.forEach(container => {
    if (container) {
      container.removeEventListener('scroll', handleScroll, true);
    }
  });
});

const handleSave = (data) => {
  emit("save", data);
  closeEditor();
};

const handleRemove = () => {
  emit("remove");
  closeEditor();
};

const handleSetToGoodStanding = () => {
  emit("setToGoodStanding");
  closeEditor();
};

const handleMakeObsolete = (data) => {
  emit("makeObsolete", data);
  closeEditor();
};
</script> 
<style scoped>
.expiration-icon-wrapper {
  position: relative;
  display: inline-block;
  line-height: 0;
}

.expiration-icon-container {
  width: fit-content;
  display: inline-block;
  cursor: pointer;
  position: relative;
  padding: 2px;
  border-radius: 4px;
  line-height: 0;
}

.default-icon {
  opacity: 1;
  transition: opacity 0.2s ease;
  display: block;
}

.hover-icon {
  position: absolute;
  top: 2px;
  left: 2px;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.expiration-icon-container:hover .default-icon {
  opacity: 0;
}

.expiration-icon-container:hover .hover-icon {
  opacity: 1;
}
</style>
<style>
.expiration-editor-menu {
  box-shadow: none !important;
  contain: initial !important;
  overflow: visible !important;
  z-index: 9999 !important;
}
.expiration-editor-menu .v-menu__content {
  box-shadow: none !important;
  overflow: visible !important;
  contain: none !important;
  z-index: 9999 !important;
}
.expiration-editor-menu .expiration-date-editor {
  overflow: visible !important;
  z-index: 10000 !important;
  position: relative;
}
.expiration-icon-wrapper .read-only-icon .read-only-icon-container {
  cursor: not-allowed;
}
</style>
